import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toast } from 'react-hot-toast';

export enum UserRole {
  USER = 'user',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  ACCOUNT_MANAGER = 'account_manager',
  CUSTOMER_SUPPORT = 'customer_support',
  AUDITOR = 'auditor'
}

interface User {
  id: string;
  email: string;
  username: string;
  isAdmin: boolean;
  role: UserRole;
  isSuperAdmin: boolean;
  isAdminOrAbove: boolean;
  hasAdminAccess: boolean;
  hasViewOnlyAccess: boolean;
  hasAuditAccess: boolean;
  emailVerified: boolean;
  phoneNumber?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;
  setToken: (token: string | null) => void;
  refreshUser: () => Promise<void>;
  resetIdleTimer: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      
      login: (user: User, token: string) => {
        // Clear any old auth data first to prevent stale data issues
        localStorage.removeItem('auth-storage');
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },
      
      logout: () => {
        set({ isAuthenticated: false, token: null, user: null });
        localStorage.removeItem('token');
      },
      
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
      
      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },
      setToken: (token: string | null) => {
        const isAuthenticated = Boolean(token);
        set({ token, isAuthenticated });
      },
      
      resetIdleTimer: () => {
        resetTimer();
      },
      
      refreshUser: async () => {
        const token = get().token;
        const currentUser = get().user;
        if (!token || !get().isAuthenticated) return;
        
        try {
          set({ isLoading: true });
          const { authAPI } = await import('../services/api');
          const userData = await authAPI.getCurrentUser();
          
          // Validate that the returned user matches the current token
          // If we have a current user, ensure the email matches (prevents showing wrong user)
          if (currentUser && userData.email !== currentUser.email) {
            console.error('User mismatch detected! Expected:', currentUser.email, 'Got:', userData.email);
            // Don't update if there's a mismatch - keep current user data
            set({ isLoading: false });
            return;
          }
          
          set({ 
            user: userData,
            isLoading: false 
          });
        } catch (error) {
          console.error('Failed to refresh user data:', error);
          const status = (error as any)?.response?.status;
          if (status === 401 || status === 403) {
            set({ isLoading: false });
            get().logout();
            return;
          }
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ===== Inactivity timer =====
let logoutTimer: any;
const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 hours

const resetTimer = () => {
  if (logoutTimer) clearTimeout(logoutTimer);
  logoutTimer = setTimeout(() => {
    if (useAuthStore.getState().isAuthenticated) {
      toast('Logged out due to inactivity', { icon: '⏲️' });
      useAuthStore.getState().logout();
    }
  }, IDLE_LIMIT_MS);
};

// Attach global listeners once
if (typeof window !== 'undefined' && !(window as any).__auraIdleAttached) {
  ['mousemove','keydown','mousedown','touchstart'].forEach(evt => {
    window.addEventListener(evt, resetTimer);
  });
  (window as any).__auraIdleAttached = true;
}

// start timer initially
resetTimer(); 
